import { Button } from "@/src/components/ui/button";
import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { InputNumber } from "@/src/components/ui/input-number";
import { Textarea } from "@/src/components/ui/textarea";
import { Combobox } from "@/src/components/ui/combobox";
import { fetch } from "@/src/lib/fetch";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { YachtForm } from "./yacht.schema";

type CrewMember = YachtForm["meta"]["crewInfo"]["members"][number];
type PerGuestRate = YachtForm["meta"]["perGuestRates"][number];
type CmsBroker = {
  documentId: string;
  name: string;
};

const emptyCrewMember: CrewMember = {
  img: "",
  name: "",
  title: "",
  nationality: "",
  bio: "",
};

const emptyPerGuestRate: PerGuestRate = {
  seasonName: "",
  terms: "",
  mcaCompliance: "",
  items: [],
};

export function MetaDetails() {
  const form = useFormContext<YachtForm>();
  const keyFeatures = form.watch("meta.keyFeatures") ?? [];
  const sampleMenuImages = form.watch("meta.sampleMenu.images") ?? [];
  const crewMembers = form.watch("meta.crewInfo.members") ?? [];
  const perGuestRates = form.watch("meta.perGuestRates") ?? [];

  return (
    <div className="col-span-full grid grid-cols-3 gap-6 border-t border-slate-200 pt-6">
      <h3 className="text-lg font-semibold col-span-full">Additional meta</h3>

      <FormField<YachtForm, "meta.checkTimes.checkIn">
        name="meta.checkTimes.checkIn"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Check-in time</FormLabel>
            <FormControl>
              <Input type="time" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<YachtForm, "meta.checkTimes.checkOut">
        name="meta.checkTimes.checkOut"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Check-out time</FormLabel>
            <FormControl>
              <Input type="time" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField<YachtForm, "meta.crewInfo.count">
        name="meta.crewInfo.count"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Crew count</FormLabel>
            <FormControl>
              <InputNumber {...field} value={field.value ?? 0} onChange={(value) => field.onChange(value || null)} />
            </FormControl>
          </FormItem>
        )}
      />

      <TextInput name="meta.brochureLink" label="Brochure link" />
      <TextInput name="meta.videoUrl" label="Video URL" />
      <TextInput name="meta.planUrl" label="Plan URL" />
      <TextInput name="meta.summerPorts" label="Summer ports" />
      <TextInput name="meta.winterPorts" label="Winter ports" />
      <TextInput name="meta.summerAreas" label="Summer areas" />
      <TextInput name="meta.winterAreas" label="Winter areas" />
      <TextInput name="meta.crewInfo.photo" label="Crew photo URL" />

      <TextAreaInput name="meta.accomodations" label="Accommodations" />
      <TextAreaInput name="meta.otherToys" label="Other toys" />
      <TextAreaInput name="meta.crewInfo.description" label="Crew description" />
      <TextAreaInput name="meta.crewInfo.languages" label="Crew languages" />
      <TextAreaInput name="meta.sampleMenu.description" label="Sample menu description" />
      <TextAreaInput name="meta.cmsBrokerNote.text" label="CMS broker note" />
      <BrokerSelect />

      <StringList
        label="Key features"
        values={keyFeatures}
        onChange={(values) => form.setValue("meta.keyFeatures", values, { shouldDirty: true })}
      />
      <StringList
        label="Sample menu images"
        values={sampleMenuImages}
        onChange={(values) => form.setValue("meta.sampleMenu.images", values, { shouldDirty: true })}
      />

      <CrewList
        title="Crew members"
        members={crewMembers}
        onChange={(members) => form.setValue("meta.crewInfo.members", members, { shouldDirty: true })}
      />
      <PerGuestRates
        rates={perGuestRates}
        onChange={(rates) => form.setValue("meta.perGuestRates", rates, { shouldDirty: true })}
      />
    </div>
  );
}

function BrokerSelect() {
  const [brokers, setBrokers] = useState<CmsBroker[]>([]);

  const loadBrokers = async (query = "") => {
    const response = await fetch<{ data: CmsBroker[] }>("/api/users/cms-brokers");
    const data = response.data ?? [];
    setBrokers(data);

    const normalizedQuery = query.trim().toLowerCase();
    return normalizedQuery ? data.filter((broker) => broker.name.toLowerCase().includes(normalizedQuery)) : data;
  };

  useEffect(() => {
    loadBrokers();
  }, []);

  return (
    <FormField<YachtForm, "meta.brokersNote.brokerId">
      name="meta.brokersNote.brokerId"
      render={({ field }) => {
        const selectedBroker =
          brokers.find((broker) => broker.documentId === field.value) ??
          (field.value ? { documentId: field.value, name: field.value } : null);

        return (
          <FormItem>
            <FormLabel>Broker</FormLabel>
            <FormControl>
              <Combobox<CmsBroker>
                className="w-full"
                placeholder="Select broker"
                value={selectedBroker}
                loadOptions={loadBrokers}
                renderOption={(broker) => broker.name}
                onChange={(broker) => field.onChange(broker?.documentId ?? "")}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
}

function TextInput({ name, label }: { name: any; label: string }) {
  return (
    <FormField
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...field} value={field.value ?? ""} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function TextAreaInput({ name, label }: { name: any; label: string }) {
  return (
    <FormField
      name={name}
      render={({ field }) => (
        <FormItem className="col-span-full">
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea className="resize-none" {...field} value={field.value ?? ""} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function StringList({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <div className="col-span-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{label}</h4>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...values, ""])}>
          <PlusIcon size={16} />
          Add
        </Button>
      </div>
      {values.map((value, index) => (
        <div key={index} className="flex gap-2">
          <Input
            value={value}
            onChange={(event) =>
              onChange(values.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)))
            }
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2Icon size={16} />
          </Button>
        </div>
      ))}
    </div>
  );
}

function CrewList({
  title,
  members,
  onChange,
}: {
  title: string;
  members: CrewMember[];
  onChange: (members: CrewMember[]) => void;
}) {
  return (
    <div className="col-span-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{title}</h4>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...members, emptyCrewMember])}>
          <PlusIcon size={16} />
          Add
        </Button>
      </div>
      {members.map((member, index) => (
        <div key={index} className="grid grid-cols-2 gap-3 rounded-md border border-slate-200 p-3">
          <Input
            placeholder="Name"
            value={member.name}
            onChange={(event) => updateItem(members, index, { name: event.target.value }, onChange)}
          />
          <Input
            placeholder="Title"
            value={member.title}
            onChange={(event) => updateItem(members, index, { title: event.target.value }, onChange)}
          />
          <Input
            placeholder="Nationality"
            value={member.nationality}
            onChange={(event) => updateItem(members, index, { nationality: event.target.value }, onChange)}
          />
          <Input
            placeholder="Image URL"
            value={member.img}
            onChange={(event) => updateItem(members, index, { img: event.target.value }, onChange)}
          />
          <Textarea
            className="col-span-full resize-none"
            placeholder="Bio"
            value={member.bio}
            onChange={(event) => updateItem(members, index, { bio: event.target.value }, onChange)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-self-start"
            onClick={() => onChange(members.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2Icon size={16} />
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}

function PerGuestRates({ rates, onChange }: { rates: PerGuestRate[]; onChange: (rates: PerGuestRate[]) => void }) {
  return (
    <div className="col-span-full flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Per guest rates</h4>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rates, emptyPerGuestRate])}>
          <PlusIcon size={16} />
          Add
        </Button>
      </div>
      {rates.map((rate, index) => (
        <div key={index} className="grid grid-cols-3 gap-3 rounded-md border border-slate-200 p-3">
          <Input
            placeholder="Season name"
            value={rate.seasonName}
            onChange={(event) => updateItem(rates, index, { seasonName: event.target.value }, onChange)}
          />
          <Input
            placeholder="Terms"
            value={rate.terms}
            onChange={(event) => updateItem(rates, index, { terms: event.target.value }, onChange)}
          />
          <Input
            placeholder="MCA compliance"
            value={rate.mcaCompliance}
            onChange={(event) => updateItem(rates, index, { mcaCompliance: event.target.value }, onChange)}
          />
          <div className="col-span-full flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Items</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => updateItem(rates, index, { items: [...rate.items, { guests: 0, rate: "" }] }, onChange)}
              >
                <PlusIcon size={16} />
                Add item
              </Button>
            </div>
            {rate.items.map((item, itemIndex) => (
              <div key={itemIndex} className="grid grid-cols-[120px_1fr_auto] gap-2">
                <InputNumber
                  value={item.guests}
                  onChange={(value) =>
                    updateItem(
                      rates,
                      index,
                      {
                        items: rate.items.map((current, i) =>
                          i === itemIndex ? { ...current, guests: value } : current,
                        ),
                      },
                      onChange,
                    )
                  }
                />
                <Input
                  placeholder="Rate"
                  value={item.rate}
                  onChange={(event) =>
                    updateItem(
                      rates,
                      index,
                      {
                        items: rate.items.map((current, i) =>
                          i === itemIndex ? { ...current, rate: event.target.value } : current,
                        ),
                      },
                      onChange,
                    )
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    updateItem(rates, index, { items: rate.items.filter((_, i) => i !== itemIndex) }, onChange)
                  }
                >
                  <Trash2Icon size={16} />
                </Button>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-self-start"
            onClick={() => onChange(rates.filter((_, i) => i !== index))}
          >
            <Trash2Icon size={16} />
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}

function updateItem<T>(items: T[], index: number, patch: Partial<T>, onChange: (items: T[]) => void) {
  onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));
}
